import { useState, type ReactNode } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { HttpStatusCode, type AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { Form } from '@components/form';
import {
  interventionSatisfactionAspects,
  interventionSatisfactionWithoutQualityAspects,
  nonQualityInterventionStatuses,
  qualitySurveyFieldId,
  satisfactionOptionsValues,
} from '@constants/survey.constant';
import {
  ESurveyEntryPoint,
  ESurveyGroup,
  ESurveyType,
} from '@enums/survey.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { qualitySurveyTokenDecoder } from '@helpers/qualitySurveyToken.helper';
import { useIsMobile } from '@hooks/useIsMobile.hook';
import { cn } from '@lib/utils';
import {
  completedSurveySchema,
  type CompletedSurveyDto,
  type SubmitSurveyPayload,
} from '@services/surveys/dtos/submit.dto';
import { surveyService } from '@services/surveys/service';
import type { SubmitSurveyOutput } from '@services/surveys/types/submit.type';

import { FollowUpRequested } from '../@components/fields/FollowUpRequested';
import { InterventionSatisfaction } from '../@components/fields/InterventionSatisfaction';
import { OpenFeedback } from '../@components/fields/OpenFeedback';
import { GroupHeader } from '../@components/GroupHeader';
import { NavigationButtons } from '../@components/NavigationButtons';

const surveySearchParamsSchema = z.object({
  entry: z.enum(ESurveyEntryPoint).optional(),
  token: z.string().optional(),
});

export const Route = createFileRoute('/_layout/$lang/quality-survey/_layout/')({
  component: RouteComponent,
  validateSearch: search => surveySearchParamsSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => {
    const decodedToken = qualitySurveyTokenDecoder(deps.token!);

    return {
      data: decodedToken,
      isNonQuality: nonQualityInterventionStatuses.has(
        decodedToken.parentStatus,
      ),
      entry: deps.entry,
    };
  },
});

function RouteComponent() {
  const { data, entry, isNonQuality } = Route.useLoaderData();
  const { lang } = Route.useParams();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const [currentStep, setCurrentStep] = useState<number>(0);

  const form = useForm<CompletedSurveyDto>({
    defaultValues: {
      communication: data.type === ESurveyType.Intervention ? '' : undefined,
      commitment: data.type === ESurveyType.Intervention ? '' : undefined,
      followUpRequested: false,
      openFeedback: '',
      quality:
        data.type === ESurveyType.Intervention && !isNonQuality
          ? ''
          : undefined,
      speed: data.type === ESurveyType.Intervention ? '' : undefined,
    },
    mode: 'onChange',
    resolver: zodResolver(completedSurveySchema),
    shouldUnregister: true,
  });

  const { isPending: isSubmittingSurvey, mutate: submitSurvey } = useMutation<
    SubmitSurveyOutput,
    AxiosError,
    SubmitSurveyPayload
  >({
    mutationFn: surveyService.submit,
    onError: error => {
      if (error.status === HttpStatusCode.Gone) {
        throw redirect({
          to: '/$lang/quality-survey/expired',
          params: {
            lang,
          },
        });
      }

      handleRequestError(error);
    },
    onSuccess: response => {
      navigate({
        params: { lang },
        to: '/$lang/quality-survey/submitted',
        search: {
          overallSatisfaction: response.overallSatisfaction,
        },
        replace: true,
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    submitSurvey({
      body: {
        commitment: values.commitment,
        communication: values.communication,
        openFeedback: values.openFeedback,
        quality: values.quality,
        speed: values.speed,
        followUpRequested: values.followUpRequested,
      },
      id: data.id,
    });
  });

  const visibleGroups = buildCompletedSurveyGroups({
    entry,
    excludeQuality: isNonQuality,
    goNext,
    isMobile,
    providerName: data.providerName,
    surveyType: data.type,
  });

  const safeStep = Math.min(currentStep, visibleGroups.length - 1);
  const isLastStep = safeStep === visibleGroups.length - 1;

  async function goNext() {
    const currentGroup = visibleGroups[safeStep];
    const isValid = await form.trigger(currentGroup.fieldIds);

    if (!isValid) {
      return;
    }

    setCurrentStep(prev => prev + 1);
  }

  function goBack() {
    setCurrentStep(prev => prev - 1);
  }

  return (
    <Form.Provider {...form}>
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div
          className={cn('flex flex-col gap-8 py-6', {
            'pb-4': isMobile,
          })}
        >
          {data.providerName && (
            <h3 className="text-lg font-semibold">{data.providerName}</h3>
          )}

          <div>
            {visibleGroups.map((group, index) => (
              <div
                className={cn('w-full', { hidden: index !== safeStep })}
                key={group.id}
              >
                <section className="flex flex-col">
                  <GroupHeader title={group.title} titleKey={group.titleKey} />
                  {group.content}
                </section>
              </div>
            ))}
          </div>
        </div>
        <NavigationButtons
          canGoBack={safeStep > 0}
          isLastStep={isLastStep}
          isLoading={isSubmittingSurvey}
          onBack={goBack}
          onNext={goNext}
          onSubmit={handleSubmit}
        />
      </div>
    </Form.Provider>
  );
}

type SurveyGroup = {
  content: ReactNode;
  fieldIds: Array<keyof CompletedSurveyDto>;
  id: string;
  title?: string;
  titleKey: string;
};

function buildCompletedSurveyGroups({
  entry,
  excludeQuality,
  goNext,
  isMobile,
  providerName,
  surveyType,
}: {
  entry?: ESurveyEntryPoint;
  excludeQuality: boolean;
  goNext: VoidFunction;
  isMobile: boolean;
  providerName?: string;
  surveyType: ESurveyType;
}): Array<SurveyGroup> {
  const isLowScore = Boolean(
    entry && Number(satisfactionOptionsValues[entry]) <= 2,
  );

  const feedbackGroup: SurveyGroup = {
    content: (
      <div className="flex flex-col gap-6">
        <OpenFeedback />
        {isLowScore && <FollowUpRequested />}
      </div>
    ),
    fieldIds: isLowScore
      ? [
          qualitySurveyFieldId.openFeedback,
          qualitySurveyFieldId.followUpRequested,
        ]
      : [qualitySurveyFieldId.openFeedback],
    id: ESurveyGroup.Feedback,
    titleKey: 'survey.group.feedback',
  };

  if (surveyType !== ESurveyType.Intervention) {
    return [feedbackGroup];
  }

  const aspects = excludeQuality
    ? interventionSatisfactionWithoutQualityAspects
    : interventionSatisfactionAspects;

  if (isMobile) {
    return [
      ...aspects.map(aspect => ({
        content: (
          <InterventionSatisfaction aspects={[aspect]} handleNext={goNext} />
        ),
        fieldIds: [qualitySurveyFieldId[aspect]] as Array<
          keyof CompletedSurveyDto
        >,
        id: `${ESurveyGroup.Intervention}-${aspect}`,
        title: providerName,
        titleKey: 'survey.group.intervention',
      })),
      feedbackGroup,
    ];
  }

  return [
    {
      content: (
        <InterventionSatisfaction aspects={aspects} handleNext={goNext} />
      ),
      fieldIds: aspects.map(a => qualitySurveyFieldId[a]),
      id: ESurveyGroup.Intervention,
      title: providerName,
      titleKey: 'survey.group.intervention',
    },
    feedbackGroup,
  ];
}
